import { createReduxEnhancer } from '@sentry/react';
import { applyMiddleware } from 'redux';
import { thunk } from 'redux-thunk';
import { routerMiddleware } from 'react-router-redux';
import { configureStore } from '@reduxjs/toolkit';

import loaderMiddleware from './app/middlewares/loaderMiddleware';
import createRootReducer from './reducer';
import authInterceptor from './app/middlewares/authInterceptor';
import sessionMiddleware from './app/middlewares/sessionMiddleware';
import windowMiddleware from './app/middlewares/windowMiddleware';
import eventsMiddleware from './app/middlewares/eventsMiddleware';
import chargeAppointmentMiddleware from './app/middlewares/chargeAppointmentMiddleware';
import tippingScreenMiddleware from './app/middlewares/tippingScreenMiddleware';
import receiptMiddleware from './app/customer/receipts/middleware';
import listeningModeMiddleware from './app/listeningMode/middleware';
import handoffPlusMiddleware from './app/handoff/handoffPlusMiddleware';
import saleFlowMiddleware from './app/saleFlow/middleware';
import saleMiddleware from './app/middlewares/saleMiddleware';
import customChargeMiddleware from './app/register/customCharge/middleware';
import giftCardMiddleware from './app/register/giftCards/middleware';
import paymentProcessingMiddleware from './app/middlewares/paymentProcessingMiddleware';
import iosBridgeMiddleware from './app/iosBridge/iosBridgeMiddleware';
import resetDateFilterMiddleware from './app/middlewares/resetDateFilterMiddleware';
import terminalMiddleware from './app/terminal/terminalMiddleware';
import refundMiddleware from './app/common/RefundModal/refundMiddleware';
import analyticsMiddleware from './app/middlewares/analyticsMiddleware';
import kioskCartSyncMiddleware from './app/kioskCartSync/kioskCartSyncMiddleware';

export default function setUpStore(initialState, history) {
  const middlewares = [
    authInterceptor,
    thunk,
    loaderMiddleware(),
    sessionMiddleware,
    routerMiddleware(history),
    windowMiddleware,
    eventsMiddleware,
    chargeAppointmentMiddleware,
    tippingScreenMiddleware,
    receiptMiddleware,
    listeningModeMiddleware,
    handoffPlusMiddleware,
    saleFlowMiddleware,
    saleMiddleware,
    customChargeMiddleware,
    giftCardMiddleware,
    paymentProcessingMiddleware,
    iosBridgeMiddleware,
    resetDateFilterMiddleware,
    terminalMiddleware,
    refundMiddleware,
    analyticsMiddleware,
    kioskCartSyncMiddleware,
  ];

  const enhancers = [
    applyMiddleware(...middlewares),
  ];

  if (!['development', 'test'].includes(process.env.NODE_ENV)) {
    const sentryReduxEnhancer = createReduxEnhancer({
      configureScopeWithState: (scope, state) => {
        scope.setUser(state.auth.user);
      },
    });

    enhancers.push(sentryReduxEnhancer);
  }

  const store = configureStore({
    reducer: createRootReducer(),
    preloadedState: initialState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        // we have to disable immutableCheck and serializableCheck because of the legacy code
        // to avoid it throwing errors
        immutableCheck: false,
        serializableCheck: false,
        actionCreatorCheck: false,
      }).concat(middlewares).filter(Boolean),
  });

  store.asyncReducers = {};
  store.injectReducer = (key, reducer) => {
    store.asyncReducers[key] = reducer;
    store.replaceReducer(createRootReducer(store.asyncReducers));
  };

  if (module.hot) {
    module.hot.accept('./reducer', () => {
      const nextReducer = require('./reducer').default; // eslint-disable-line global-require
      store.replaceReducer(nextReducer);
    });
  }

  return store;
}
